import * as React from "react";

import {
  fromPlayerPost,
  fromPrompt,
  fromVideoProject,
  SharedPostFlowDefaults,
} from "@/components/PostDialogs/SharedPostFlowDefaults";
import { PlayerPostDialog } from "@/components/PostDialogs/PlayerPostDialog";
import { ReadyToPostContentType } from "@/interfaces";
import { PostServices, PromptServices, VideoProjectServices } from "@/services";

import { Spinner } from "@/components/ui";
import { toast } from "sonner";
interface OwnProps {
  opened: boolean;
  contentId: number;
  contentType: ReadyToPostContentType;
  thumbnail: string;
  closeHandler: () => void;
}

export function PostAnythingDialog({ opened, contentId, contentType, closeHandler }: OwnProps) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [postDefaults, setPostDefaults] = React.useState<SharedPostFlowDefaults>();
  const [postId, setPostId] = React.useState<number | string>();

  const handleError = (e: any) => {
    toast.error("There was a problem fetching this post", { description: e.message || "Unknown error" });
  };

  const getPrompt = () => {
    setLoading(true);
    PromptServices.get(contentId)
      .then(promptResponse => {
        setPostDefaults(fromPrompt(promptResponse));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getProject = () => {
    setLoading(true);
    VideoProjectServices.get(contentId)
      .then(videoProject => {
        setPostId(videoProject.post?.postId);
        setPostDefaults(fromVideoProject(videoProject));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getPost = () => {
    setLoading(true);
    PostServices.getById(contentId)
      .then(post => {
        setPostId(post.postId);
        if (post.promptId) {
          PromptServices.get(post.promptId)
            .then(prompt => {
              setPostDefaults(fromPlayerPost(post, prompt));
            })
            .catch(handleError);
        } else {
          setPostDefaults(fromPlayerPost(post));
        }
      })
      .finally(() => {
        setLoading(false);
      })
      .catch(handleError);
  };

  React.useEffect(() => {
    switch (contentType) {
      case "PROMPT":
        getPrompt();
        break;
      case "VIDEO_PROJECT":
        getProject();
        break;
      case "POST":
        getPost();
        break;
    }
  }, [contentId, contentType]);

  if (opened && loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/10 backdrop-blur-[2px] animate-in fade-in duration-200">
        <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center border border-slate-100 animate-in zoom-in-95 duration-300">
          <Spinner className="size-10 mb-4 text-primary" />
          <p className="text-lg font-bold text-slate-800">Loading Post Details</p>
          <p className="text text-slate-500 mt-1">Taking you there in a second...</p>
        </div>
      </div>
    );
  }

  if (!postDefaults) return null;

  return <PlayerPostDialog open={opened} postId={postId} handleClose={closeHandler} defaults={postDefaults} />;
}
